import { faArrowRightFromBracket, faCog } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Colors } from '@vetahealth/fishing-gear/colors'
import { isTruthy } from '@vetahealth/fishing-gear/lib/typeguards'
import { PermissionName } from '@vetahealth/fishing-gear/permissions'
import { Avatar, Button, Dropdown, Tooltip } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { TrackingEvent, trackEvent } from '../lib/tracking'
import { routes } from '../Router/routes'
import { SiteManagementTab } from '../Router/routes/SiteManagement'
import { useAuthStore } from '../stores/auth'
import { ModalName, useModalStore } from '../stores/modal'
import { useUserStore } from '../stores/user'

const Name = styled.div`
  font-weight: 600;
  color: ${Colors.gray900};
`

const Email = styled.div`
  color: ${Colors.gray500};
  font-size: 13px;
`

enum MenuKeys {
  addPatient = 'addPatient',
  careSummaryReport = 'careSummaryReport',
  importCsv = 'importCsv',
  manualOrders = 'manualOrders',
  resetDemoPatients = 'resetDemoPatients',
  resetUserSettings = 'resetUserSettings',
  preview = 'preview',
  userManagement = 'userManagement',
  siteManagement = 'siteManagement',
  roleManagement = 'roleManagement',
  archive = 'archive',
  analytics = 'analytics',
  settings = 'settings',
  signOut = 'signOut',
}

export function Menu(): React.ReactNode {
  const { t } = useTranslation()
  const handleSignout = useAuthStore((state) => state.handleSignout)
  const [hasSomePermission, hasPermission, firstName, lastName, email] = useUserStore((state) => [
    state.hasSomePermission,
    state.hasPermission,
    state.firstName,
    state.lastName,
    state.email,
  ])

  const [openModal] = useModalStore((state) => [state.openModal])
  const navigate = useNavigate()
  const [isMenuVisible, setIsMenuVisible] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  function handleVisibilityChange(visible: boolean): void {
    setIsMenuVisible(visible)
    if (visible) setShowTooltip(false)
  }

  function handleSelection({ key }: { key: string }): void {
    setIsMenuVisible(false)
    switch (key) {
      case MenuKeys.settings:
        trackEvent(TrackingEvent.profileMenuSettingsClicked)
        navigate(routes.settings())
        return
      case MenuKeys.addPatient:
        trackEvent(TrackingEvent.settingsMenuAddPatientClicked)
        openModal(ModalName.addPatient)
        return
      case MenuKeys.importCsv:
        trackEvent(TrackingEvent.settingsMenuImportPatientsClicked)
        navigate(routes.csvImport())
        return
      case MenuKeys.careSummaryReport:
        trackEvent(TrackingEvent.settingsMenuCareSummaryReportClicked)
        openModal(ModalName.reportWizard)
        return
      case MenuKeys.siteManagement:
        trackEvent(TrackingEvent.settingsMenuSiteMgmtClicked)
        navigate(routes.siteManagement(SiteManagementTab.programs))
        return
      case MenuKeys.userManagement:
        trackEvent(TrackingEvent.settingsMenuUserMgmtClicked)
        navigate(routes.userManagement())
        return
      case MenuKeys.roleManagement:
        trackEvent(TrackingEvent.settingsMenuRoleMgmtClicked)
        navigate(routes.roleManagement())
        return
      case MenuKeys.resetDemoPatients:
        openModal(ModalName.resetDemoPatients)
        return
      case MenuKeys.resetUserSettings:
        openModal(ModalName.resetUserSettings)
        return
      case MenuKeys.preview:
        openModal(ModalName.preview)
        return
      case MenuKeys.manualOrders:
        trackEvent(TrackingEvent.settingsMenuManualOrdersClicked)
        navigate(routes.manualOrders())
        return
      case MenuKeys.archive:
        trackEvent(TrackingEvent.settingsMenuArchiveClicked)
        navigate(routes.archive())
        return
      case MenuKeys.analytics:
        trackEvent(TrackingEvent.settingsMenuAnalyticsClicked)
        navigate(routes.analytics())
        return
      case MenuKeys.signOut:
        trackEvent(TrackingEvent.profileMenuSignoutClicked)
        handleSignout()
        return
    }
  }

  const menuItems: any[] = [
    {
      key: 'account',
      label: (
        <>
          <Name>
            {firstName} {lastName}
          </Name>
          <Email>{email}</Email>
        </>
      ),
      style: {
        pointerEvents: 'none',
        cursor: 'default',
      },
      disabled: true,
    },

    {
      key: MenuKeys.settings,
      label: t('menus.main.settings'),
      icon: <FontAwesomeIcon icon={faCog} />,
    },
    {
      type: 'divider',
    },
    (hasSomePermission(PermissionName.accessPatient) || hasSomePermission(PermissionName.dispatchManualOrder)) && {
      type: 'group',
      key: 'patient',
      label: t('menus.main.patients'),
      children: [
        hasSomePermission(PermissionName.accessPatient) && {
          key: MenuKeys.addPatient,
          label: t('menus.main.addPatient'),
        },
        hasSomePermission(PermissionName.accessPatient) && {
          key: MenuKeys.importCsv,
          label: t('menus.main.importCsv'),
        },
        { key: MenuKeys.archive, label: t('menus.main.archive') },
        hasSomePermission(PermissionName.dispatchManualOrder) && {
          key: MenuKeys.manualOrders,
          label: t('menus.main.manualOrders'),
        },
        hasSomePermission(PermissionName.accessAnalytics) && {
          key: MenuKeys.analytics,
          label: t('menus.main.analytics'),
        },
      ],
    },
    (hasSomePermission(PermissionName.manageSite) ||
      hasSomePermission(PermissionName.manageUser) ||
      hasPermission(PermissionName.manageRole)) && {
      type: 'group',
      key: 'management',
      label: t('menus.main.management'),
      children: [
        hasSomePermission(PermissionName.manageSite) && {
          key: MenuKeys.siteManagement,
          label: t('menus.main.siteManagement'),
        },
        hasSomePermission(PermissionName.manageUser) && {
          key: MenuKeys.userManagement,
          label: t('menus.main.userManagement'),
        },
        hasPermission(PermissionName.manageRole) && {
          key: MenuKeys.roleManagement,
          label: t('menus.main.roleManagement'),
        },
      ],
    },
    hasSomePermission(PermissionName.testFeatures) && {
      type: 'group',
      key: 'testing',
      label: t('menus.main.testing'),
      children: [
        { key: MenuKeys.resetDemoPatients, label: t('menus.main.resetDemoPatients') },
        { key: MenuKeys.resetUserSettings, label: t('menus.main.resetUserSettings') },
        { key: MenuKeys.preview, label: t('menus.main.preview') },
      ],
    },
    {
      type: 'divider',
    },
    {
      key: MenuKeys.signOut,
      label: t('menus.main.signOut'),
      icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
    },
  ].filter(isTruthy)

  return (
    <div>
      {/** biome-ignore lint/a11y/noStaticElementInteractions: <Only used for mouse over event> */}
      <div onMouseEnter={() => setShowTooltip(!isMenuVisible)} onMouseLeave={() => setShowTooltip(false)}>
        <Dropdown
          menu={{ items: menuItems, onClick: handleSelection }}
          trigger={['click']}
          placement="bottomLeft"
          onOpenChange={handleVisibilityChange}
        >
          <Tooltip title={t('menus.main.title')} open={showTooltip} placement="bottomLeft">
            <Button
              shape="circle"
              type="text"
              size="large"
              onClick={() => trackEvent(TrackingEvent.settingsMenuClicked)}
            >
              <Avatar size={36} style={{ color: Colors.mauve800, backgroundColor: Colors.mauve200 }}>
                {firstName?.[0]}
                {lastName?.[0]}
              </Avatar>
            </Button>
          </Tooltip>
        </Dropdown>
      </div>
    </div>
  )
}
